/** Synthetic projects for demos and performance benchmarks. */
import { applyTransaction, createProject, newId, type Locale, type Op, type Project } from '@elec/domain';

const PALETTE = [
  'iec.breaker',
  'iec.rcd',
  'iec.fuse',
  'iec.contact-no',
  'iec.contact-nc',
  'iec.coil',
  'iec.light',
  'iec.motor',
];

/**
 * A grid of columns, each a vertical chain of symbols linked by wires.
 * Produces roughly `count` elements and `count` wires.
 */
export function generateBenchmarkProject(count: number, locale: Locale = 'fr'): Project {
  const p = createProject({ title: `Benchmark ${count}`, locale });
  const sheetId = Object.keys(p.sheets)[0]!;
  const perColumn = 25;
  const ops: Op[] = [];
  let prev: string | undefined;
  for (let i = 0; i < count; i++) {
    const col = Math.floor(i / perColumn);
    const row = i % perColumn;
    const symbolId = row === perColumn - 1 ? 'iec.light' : PALETTE[(i * 7 + col) % (PALETTE.length - 2)]!;
    const id = newId();
    ops.push({
      op: 'add',
      coll: 'elements',
      value: {
        id,
        sheetId,
        symbolId,
        x: col * 25,
        y: row * 30,
        rotation: 0,
        mirrored: false,
        tag: `Q${i + 1}`,
        props: {},
      },
    });
    if (row > 0 && prev) {
      ops.push({
        op: 'add',
        coll: 'wires',
        value: {
          id: newId(),
          sheetId,
          from: { kind: 'port', elementId: prev, portId: 'out' },
          to: { kind: 'port', elementId: id, portId: 'in' },
          via: [],
        },
      });
    } else if (col > 0 && prev) {
      // A bus wire across the top joining the columns.
      ops.push({
        op: 'add',
        coll: 'wires',
        value: {
          id: newId(),
          sheetId,
          from: { kind: 'point', x: (col - 1) * 25 + 5, y: -5 },
          to: { kind: 'point', x: col * 25 + 5, y: -5 },
          via: [],
        },
      });
    }
    prev = id;
  }
  return applyTransaction(p, { label: 'generate', ops }).project;
}
